// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module useAnalytics
 * @package @geenius/analytics/react
 * @description Exposes the primary analytics hook for the React variant. The
 * hook prefers an enclosing provider-backed tracker and falls back to a lazily
 * created standalone tracker when used outside provider context.
 */

import type {
  ConsentSettings,
  TrackerConfigInput,
  TrackerInstance,
  TrackerTrackOptions,
  UserTraits,
} from "@geenius/analytics";
import { createTracker } from "@geenius/analytics";
import { useCallback, useRef } from "react";
import { useAnalyticsContext } from "../providers/AnalyticsProvider";

/**
 * Stable analytics actions returned by {@link useAnalytics}.
 *
 * @property track Tracks a named analytics event with optional consent scope.
 * @property page Tracks a page view for a URL with optional properties.
 * @property identify Associates the current session with a user identity.
 * @property reset Clears the current analytics session state.
 * @property consent Updates analytics consent settings for the tracker.
 * @property deleteUserData Deletes a user from storage/provider integrations.
 * @property forgetUser Alias for deleteUserData for right-to-forget flows.
 */
export interface UseAnalyticsReturn {
  track: (
    name: string,
    props?: Record<string, unknown>,
    options?: TrackerTrackOptions,
  ) => Promise<void>;
  page: (url: string, props?: Record<string, unknown>) => Promise<void>;
  identify: (userId: string, traits?: UserTraits) => Promise<void>;
  reset: () => Promise<void>;
  consent: (settings: ConsentSettings) => void;
  deleteUserData: (userId: string) => Promise<void>;
  forgetUser: (userId: string) => Promise<void>;
}

/**
 * Returns the analytics actions for the current React subtree.
 *
 * When no provider is mounted above the caller, the hook creates a lazy local
 * tracker from the supplied configuration so isolated components can still
 * record events.
 *
 * @param config Optional fallback tracker configuration used outside provider context.
 * @returns Stable analytics action functions for the active tracker.
 */
export function useAnalytics(config?: TrackerConfigInput): UseAnalyticsReturn {
  const contextTracker = useAnalyticsContext({ optional: true });
  const fallbackRef = useRef<TrackerInstance | null>(null);
  const fallbackConfigRef = useRef<TrackerConfigInput | undefined>(undefined);
  const configRef = useRef(config);

  configRef.current = config;

  const getTracker = useCallback((): TrackerInstance => {
    if (contextTracker) return contextTracker;

    if (
      !fallbackRef.current ||
      fallbackConfigRef.current !== configRef.current
    ) {
      fallbackRef.current = createTracker(configRef.current ?? {});
      fallbackConfigRef.current = configRef.current;
    }

    return fallbackRef.current;
  }, [contextTracker]);

  return {
    track: useCallback(
      (
        name: string,
        props?: Record<string, unknown>,
        options?: TrackerTrackOptions,
      ) => getTracker().track(name, props, options),
      [getTracker],
    ),
    page: useCallback(
      (url: string, props?: Record<string, unknown>) =>
        getTracker().page(url, props),
      [getTracker],
    ),
    identify: useCallback(
      (userId: string, traits?: UserTraits) =>
        getTracker().identify(userId, traits),
      [getTracker],
    ),
    reset: useCallback(() => getTracker().reset(), [getTracker]),
    consent: useCallback(
      (settings: ConsentSettings) => getTracker().consent(settings),
      [getTracker],
    ),
    deleteUserData: useCallback(
      (userId: string) => getTracker().deleteUserData(userId),
      [getTracker],
    ),
    forgetUser: useCallback(
      (userId: string) => getTracker().forgetUser(userId),
      [getTracker],
    ),
  };
}
